/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import Button from 'components/generics/Button/Button';
import ConfirmDelete from 'components/generics/ConfirmDelete/ConfirmDelete';
import Modal from 'components/generics/Modal/Modal';
import RoleGuard from 'components/generics/RoleGuard/RoleGuard';
import ChecklistFillModal from 'components/generics/Checklist/ChecklistFillModal';
import ChecklistStateBadge from 'components/generics/Checklist/ChecklistStateBadge';
import DeleteIcon from 'components/icons/DeleteIcon';

import { deleteInstance, getInstancesByEntity } from 'services/API/Checklist';
import { subscribeChecklistEvents } from 'services/API/ChecklistRealtime';

import { formatDate } from 'utils/func';

import './Checklist.style.scss';

const errorMessage = err => err?.response?.data?.message || err?.message || 'Unexpected error';

/**
 * Section checklist embarquée dans les formulaires Stage / Vol / Aéroport.
 * - Stage et vol : une instance unique ;
 * - aéroport : une instance par jour.
 * Mise à jour en direct (SSE) + repli polling.
 */
const ChecklistSection = ({ entityType, entityId, canEdit = true }) => {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fillInstanceId, setFillInstanceId] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const mounted = useRef(true);

  const load = () => {
    if (!entityId) return;
    getInstancesByEntity(entityType, entityId)
      .then(res => {
        if (mounted.current) setInstances(res.data || []);
      })
      .catch(err => {
        if (mounted.current) toast.error(errorMessage(err));
      })
      .finally(() => {
        if (mounted.current) setLoading(false);
      });
  };

  useEffect(() => {
    mounted.current = true;
    load();
    const unsubscribe = subscribeChecklistEvents(event => {
      if (event.type === 'poll' || event.instanceId) {
        load();
      }
    });
    return () => {
      mounted.current = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const onDelete = () => {
    if (!deleteCandidate) return;
    deleteInstance(deleteCandidate.id, true)
      .then(() => {
        setDeleteCandidate(null);
        load();
      })
      .catch(err => toast.error(errorMessage(err)));
  };

  const closeFillModal = () => {
    setFillInstanceId(null);
    load();
  };

  return (
    <div className="checklist-section">
      <div className="checklist-section__header">
        <h3>CHECKLIST</h3>
        {!loading && instances.length === 0 && (
          <span className="checklist-section__hint">
            No checklist instance yet — use “Generate / Update checklists” in Parameters → Checklist
            models.
          </span>
        )}
      </div>
      {loading ? (
        <span className="checklist-section__loading">Loading…</span>
      ) : instances.length > 0 ? (
        <ul className="checklist-section__list">
          {instances.map(instance => (
            <li key={instance.id} className="checklist-section__row">
              <div className="checklist-section__info">
                <span className="checklist-section__date">{formatDate(new Date(instance.date))}</span>
                <span className="checklist-section__template">{instance.templateName || '—'}</span>
                <ChecklistStateBadge state={instance.state} />
              </div>
              <div className="checklist-section__actions">
                <Button
                  text={canEdit ? 'Fill' : 'View'}
                  defaultStyle="primary less_padding"
                  onClick={() => setFillInstanceId(instance.id)}
                />
                <RoleGuard allowedRoles={['Admin', 'CPSAdmin']}>
                  <button
                    type="button"
                    className="checklist-section__delete"
                    title="Delete this checklist"
                    onClick={() => setDeleteCandidate(instance)}
                  >
                    <DeleteIcon width={14} height={18} color="#fff" fill="#fff" />
                  </button>
                </RoleGuard>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      {fillInstanceId && (
        <Modal onClose={closeFillModal}>
          <ChecklistFillModal instanceId={fillInstanceId} canEdit={canEdit} onClose={closeFillModal} />
        </Modal>
      )}
      {deleteCandidate && (
        <ConfirmDelete
          onClose={() => setDeleteCandidate(null)}
          list={[deleteCandidate]}
          onConfirm={onDelete}
          formatList={instance =>
            `Checklist of ${formatDate(new Date(instance.date))} (${
              instance.templateName || 'template deleted'
            })`
          }
        />
      )}
    </div>
  );
};

export default ChecklistSection;
